import React, { useState } from 'react';
import { CheckCircleIcon, XCircleIcon, MessageSquareIcon, ClockIcon, MapPinIcon, TruckIcon, UserIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
interface ContractorJobRequest {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  material: string;
  numberOfTrucks: string;
  truckType: string;
  loadLocation: string;
  dumpLocation: string;
  foreman: string;
  foremanPhone: string;
  startTime: string;
  contractorName: string;
  contractorId: string;
  requestedDate: string;
  urgency: 'low' | 'medium' | 'high';
  estimatedHours: number;
  travelTime?: string;
  comments?: string;
  status: 'pending' | 'accepted' | 'declined';
}
interface ContractorJobRequestsProps {
  requests: ContractorJobRequest[];
  onAcceptJob: (job: ContractorJobRequest) => void;
  onDeclineJob: (jobId: string) => void;
  onNegotiateJob: (jobId: string) => void;
}
export const ContractorJobRequests = ({
  requests,
  onAcceptJob,
  onDeclineJob,
  onNegotiateJob
}: ContractorJobRequestsProps) => {
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  return <div className="bg-white rounded-md shadow p-6" data-id="element-763">
      <div className="flex items-center justify-between mb-6" data-id="element-764">
        <div data-id="element-765">
          <h2 className="text-xl font-bold" data-id="element-766">Contractor Job Requests</h2>
          <p className="text-gray-600 mt-1" data-id="element-767">
            Review and manage job assignments from contractors
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-768">
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs" data-id="element-769">
            {requests.filter(r => r.urgency === 'high').length} High Priority
          </span>
          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs" data-id="element-770">
            {requests.filter(r => r.urgency === 'medium').length} Medium
            Priority
          </span>
        </div>
      </div>
      {requests.length === 0 ? <div className="bg-gray-50 p-8 rounded-md text-center" data-id="element-771">
          <MessageSquareIcon size={40} className="mx-auto mb-3 text-gray-400" data-id="element-772" />
          <p className="text-gray-600 font-medium" data-id="element-773">No contractor requests</p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-774">
            When contractors assign jobs to you, they'll appear here for review
          </p>
        </div> : <div className="space-y-4" data-id="element-775">
          {requests.map(request => <div key={request.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-776">
              <div className="p-4" data-id="element-777">
                <div className="flex items-start justify-between" data-id="element-778">
                  <div className="flex-grow" data-id="element-779">
                    <div className="flex items-center gap-3 mb-2" data-id="element-780">
                      <h3 className="font-semibold text-lg" data-id="element-781">
                        {request.jobName}
                      </h3>
                      <span className="text-sm text-gray-500" data-id="element-782">
                        #{request.jobNumber}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(request.urgency)}`} data-id="element-783">
                        {request.urgency.toUpperCase()} PRIORITY
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3" data-id="element-784">
                      <div className="flex items-center gap-2" data-id="element-785">
                        <ClockIcon size={16} className="text-gray-400" data-id="element-786" />
                        <span data-id="element-787">
                          {formatDate(request.date)} at {request.startTime}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-788">
                        <TruckIcon size={16} className="text-gray-400" data-id="element-789" />
                        <span data-id="element-790">
                          {request.numberOfTrucks} - {request.truckType}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-791">
                        <UserIcon size={16} className="text-gray-400" data-id="element-792" />
                        <span data-id="element-793">{request.foreman}</span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-794">
                        <MapPinIcon size={16} className="text-gray-400" data-id="element-795" />
                        <span data-id="element-796">{request.material}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-797">
                      <div className="text-sm text-gray-600 flex items-center gap-4" data-id="element-798">
                        <span data-id="element-799">
                          <span className="font-medium" data-id="element-800">From:</span>{' '}
                          {request.contractorName}
                        </span>
                        <span data-id="element-801">
                          <span className="font-medium" data-id="element-802">Est. Duration:</span>{' '}
                          {request.estimatedHours} hours
                        </span>
                        {request.travelTime && <span className="flex items-center gap-1" data-id="element-803">
                            <ClockIcon size={14} className="text-orange-500" data-id="element-804" />
                            <span className="font-medium text-orange-600" data-id="element-805">
                              Travel: {request.travelTime} min
                            </span>
                          </span>}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-806">
                        <button onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)} className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-807">
                          {expandedRequest === request.id ? 'Hide Details' : 'View Details'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {expandedRequest === request.id && <div className="mt-4 pt-4 border-t bg-gray-50 -mx-4 px-4 pb-4" data-id="element-808">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4" data-id="element-809">
                      <div data-id="element-810">
                        <h4 className="font-medium mb-2" data-id="element-811">Job Details</h4>
                        <div className="space-y-2 text-sm" data-id="element-812">
                          <div className="flex items-center gap-2" data-id="element-813">
                            <MapPinIcon size={16} className="text-gray-400" data-id="element-814" />
                            <span data-id="element-815">
                              <strong data-id="element-816">Load:</strong> {request.loadLocation}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-817">
                            <MapPinIcon size={16} className="text-gray-400" data-id="element-818" />
                            <span data-id="element-819">
                              <strong data-id="element-820">Dump:</strong> {request.dumpLocation}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-821">
                            <UserIcon size={16} className="text-gray-400" data-id="element-822" />
                            <span data-id="element-823">
                              <strong data-id="element-824">Foreman:</strong> {request.foreman} (
                              {request.foremanPhone})
                            </span>
                          </div>
                        </div>
                      </div>
                      <div data-id="element-825">
                        <h4 className="font-medium mb-2" data-id="element-826">
                          Additional Information
                        </h4>
                        <div className="text-sm" data-id="element-827">
                          <p data-id="element-828">
                            <strong data-id="element-829">Requested Date:</strong>{' '}
                            {formatDate(request.requestedDate)}
                          </p>
                          <p data-id="element-830">
                            <strong data-id="element-831">Material Type:</strong> {request.material}
                          </p>
                          {request.travelTime && <p className="flex items-center gap-1" data-id="element-832">
                              <strong data-id="element-833">Travel Time:</strong>
                              <span className="flex items-center gap-1 text-orange-600" data-id="element-834">
                                <ClockIcon size={14} data-id="element-835" />
                                {request.travelTime} minutes
                              </span>
                            </p>}
                          {request.comments && <div className="mt-2" data-id="element-836">
                              <p data-id="element-837">
                                <strong data-id="element-838">Comments:</strong>
                              </p>
                              <p className="text-gray-600 italic" data-id="element-839">
                                {request.comments}
                              </p>
                            </div>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t" data-id="element-840">
                      <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-841">
                        <InfoIcon size={16} className="text-blue-500" data-id="element-842" />
                        <span data-id="element-843">
                          Review job details and assign to your trucking
                          partners
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-844">
                        <button onClick={() => onNegotiateJob(request.id)} className="flex items-center gap-2 px-3 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-845">
                          <MessageSquareIcon size={16} data-id="element-846" />
                          Negotiate
                        </button>
                        <button onClick={() => onDeclineJob(request.id)} className="flex items-center gap-2 px-3 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-50" data-id="element-847">
                          <XCircleIcon size={16} data-id="element-848" />
                          Decline
                        </button>
                        <button onClick={() => onAcceptJob(request)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700" data-id="element-849">
                          <CheckCircleIcon size={16} data-id="element-850" />
                          Accept Job
                        </button>
                      </div>
                    </div>
                  </div>}
              </div>
              {expandedRequest !== request.id && <div className="px-4 pb-4 flex items-center justify-end gap-3" data-id="element-851">
                  <button onClick={() => onNegotiateJob(request.id)} className="flex items-center gap-2 px-3 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-852">
                    <MessageSquareIcon size={16} data-id="element-853" />
                    Negotiate
                  </button>
                  <button onClick={() => onDeclineJob(request.id)} className="flex items-center gap-2 px-3 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-50" data-id="element-854">
                    <XCircleIcon size={16} data-id="element-855" />
                    Decline
                  </button>
                  <button onClick={() => onAcceptJob(request)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700" data-id="element-856">
                    <CheckCircleIcon size={16} data-id="element-857" />
                    Accept Job
                  </button>
                </div>}
            </div>)}
        </div>}
    </div>;
};